<template>
  <article class="portal-analytics-card portal-analytics-card--pie">
    <header class="portal-analytics-card__header">
      <h3 class="portal-analytics-card__title">{{ $t(config.titleKey) }}</h3>
    </header>

    <div class="portal-analytics-card__body portal-analytics-card__body--pie">
      <div class="portal-analytics-card__pie-chart">
        <Pie
          :key="selectedTireType"
          :chart-data="chartData"
          :chart-options="chartOptions"
          :plugins="pieChartPlugins"
          :style="{ height: '220px', width: '100%' }"
        />
      </div>
      <ul class="portal-analytics-card__pie-legend">
        <li v-for="(slice, index) in legendItems" :key="index">
          <span
            class="portal-analytics-card__legend-dot"
            :style="{ backgroundColor: slice.color }"
          />
          <span class="portal-analytics-card__legend-label">{{ slice.label }}</span>
        </li>
      </ul>
    </div>

    <div class="portal-analytics-card__footer">
      <PortalAnalyticsTireSelect
        v-model="selectedTireType"
        :select-id="filterId"
      />
      <PortalAnalyticsDownloadButton
        class="portal-analytics-card__download"
        :export-type="config.exportType"
      />
    </div>
  </article>
</template>

<script setup>
import { computed, defineProps, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Pie } from "vue-chartjs";
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";
import { getPortalAnalyticsPieSlices } from "@/data/portal_analytics_mock";
import PortalAnalyticsTireSelect from "@/components/PortalHome/Analytics/PortalAnalyticsTireSelect.vue";
import PortalAnalyticsDownloadButton from "@/components/PortalHome/Analytics/PortalAnalyticsDownloadButton.vue";
import {
  createPortalAnalyticsPiePercentLabelsPlugin,
  portalAnalyticsChartOptions,
} from "@/components/PortalHome/Analytics/portalAnalyticsCharts.js";

ChartJS.register(ArcElement, Tooltip);

const PIE_LABEL_MIN_PERCENT = 8;
const PIE_LABEL_FONT_SIZE_PX = 15;

const props = defineProps({
  config: { type: Object, required: true },
});

const { t } = useI18n();
const selectedTireType = ref("all");
const filterId = computed(() => `portal-analytics-filter-${props.config.id}`);

const activeSlices = computed(() =>
  getPortalAnalyticsPieSlices(props.config, selectedTireType.value),
);

const resolveSliceLabel = (slice) => {
  if (slice.label) {
    return slice.label;
  }

  if (!slice.labelKey) {
    return "";
  }

  const translated = t(slice.labelKey);
  return translated === slice.labelKey ? slice.labelKey : translated;
};

const legendItems = computed(() => {
  const total = activeSlices.value.reduce((sum, s) => sum + s.value, 0);
  return activeSlices.value.map((slice) => ({
    label: resolveSliceLabel(slice),
    color: slice.color,
    percent: total ? Math.round((slice.value / total) * 100) : 0,
    value: slice.value,
  }));
});

const chartData = computed(() => ({
  labels: legendItems.value.map((item) => item.label),
  datasets: [
    {
      data: activeSlices.value.map((s) => s.value),
      backgroundColor: activeSlices.value.map((s) => s.color),
      borderColor: "#fff",
      borderWidth: 2,
    },
  ],
}));

const pieChartPlugins = [
  createPortalAnalyticsPiePercentLabelsPlugin({
    minPercent: PIE_LABEL_MIN_PERCENT,
    fontSizePx: PIE_LABEL_FONT_SIZE_PX,
  }),
];

const chartOptions = computed(() =>
  portalAnalyticsChartOptions({
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (ctx) => {
            const item = legendItems.value[ctx.dataIndex];
            return ` ${item?.label}: ${item?.percent}%`;
          },
        },
      },
    },
  }),
);

</script>

<style scoped lang="scss">
@import "@/styles/portal-home/portal-analytics";
</style>
